// Client for /api/reports — scheduled email reports management.
// Mirrors backend/chainlit/reports_api.py (which itself mirrors the chat-side
// register/list/update/delete_report functions — one shared table).

export type ReportFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type TimePeriod = 'MORNING' | 'EVENING' | 'NIGHT';

export interface Quota {
  plan: 'FREE' | 'FREE_TRIAL' | 'PRO';
  total_used: number;
  total_limit: number;
  daily_used: number;
  daily_limit: number;
  trial_days_left: number;
  pro_total_limit: number;
  pro_daily_limit: number;
}

export interface Report {
  id: number;
  title: string;
  question: string;
  frequency: ReportFrequency;
  hour_local: number;
  minute_local: number;
  weekday: number | null;       // WEEKLY: 0=Monday … 6=Sunday
  day_of_month: number | null;  // MONTHLY (clamped by the worker)
  timezone: string;
  time_period: TimePeriod | 'EXACT' | null;
  recipient_email: string;
  active: boolean;
  test_send_used: boolean;
  last_status: string | null;   // SENT / EMPTY / FAILED_* / null
  last_run_at: string | null;
  next_run_at: string | null;
  consecutive_failures: number;
  created_at: string;
}

export interface ReportCreate {
  title: string;
  question: string;
  frequency: ReportFrequency;
  time_period?: TimePeriod;
  hour_local?: number;
  minute_local?: number;
  weekday?: number | null;
  day_of_month?: number | null;
  recipient_email: string;
  send_now: boolean;
}

export type ReportPatch = Partial<Omit<ReportCreate, 'send_now'>> & {
  active?: boolean;
};

async function jsonFetch(url: string, init?: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export async function apiListReports(): Promise<{ items: Report[] }> {
  return jsonFetch('/api/reports');
}

export async function apiCreateReport(
  body: ReportCreate
): Promise<Report & { instant_copy_queued: boolean; scheduled_next_run_at: string }> {
  return jsonFetch('/api/reports', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiPatchReport(id: number, body: ReportPatch): Promise<Report> {
  return jsonFetch(`/api/reports/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiDeleteReport(id: number): Promise<{ deleted: boolean }> {
  return jsonFetch(`/api/reports/${id}`, { method: 'DELETE' });
}

export async function apiTestSend(
  id: number
): Promise<{ queued: boolean; recipient_email: string; eta_minutes: number }> {
  return jsonFetch(`/api/reports/${id}/test-send`, { method: 'POST' });
}

export async function apiQuota(): Promise<Quota> {
  return jsonFetch('/api/reports/quota');
}

// The exact local time a window would deliver at — the user commits to a
// time, never to a vague range.
export async function apiSlotPreview(
  period: TimePeriod
): Promise<{ hour_local: number; minute_local: number }> {
  return jsonFetch(`/api/reports/slot-preview?period=${period}`);
}

export async function apiPreviewHtml(): Promise<string> {
  const res = await fetch('/api/reports/preview');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.text();
}

// The actual last sent email of a subscription (real numbers), rendered by
// the worker and stored in the send log. Returns null while nothing has been
// sent yet (404). sentAt lets the UI detect when a NEW send lands.
export async function apiLastEmail(
  id: number
): Promise<{ html: string; sentAt: string | null } | null> {
  const res = await fetch(`/api/reports/${id}/last-email`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return { html: await res.text(), sentAt: res.headers.get('X-Sent-At') };
}

// Best-effort LLM title suggestion — empty string means "no suggestion,
// fall back to whatever the user has".
export async function apiSuggestTitle(question: string): Promise<string> {
  try {
    const res = await jsonFetch('/api/reports/suggest-title', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ question })
    });
    return res.title || '';
  } catch {
    return '';
  }
}

// ── Humanizing helpers ────────────────────────────────────────────────────────

export const WEEKDAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

export const PERIOD_LABEL: Record<TimePeriod, string> = {
  MORNING: 'Morning',
  EVENING: 'Afternoon',
  NIGHT: 'Overnight'
};

export const PERIOD_HINT: Record<TimePeriod, string> = {
  MORNING: '7 AM – 12 PM',
  EVENING: '12 PM – 6 PM',
  NIGHT: '2 AM – 7 AM'
};

export function formatTime(hour: number, minute: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

export const FREQUENCY_LABEL: Record<ReportFrequency, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly'
};

export function scheduleText(r: Report): string {
  const time = formatTime(r.hour_local, r.minute_local);
  if (r.frequency === 'WEEKLY') {
    return `Weekly — ${WEEKDAYS[r.weekday ?? 0]} at ${time}`;
  }
  if (r.frequency === 'MONTHLY') {
    return `Monthly — day ${r.day_of_month ?? 1} at ${time}`;
  }
  return `Daily at ${time}`;
}

export function formatLocalDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return iso;
  }
}
